import * as Core from './core.ts';
import { finish } from './work.ts';
import type { Network, TrainPrediction } from './gps-types.ts';
import type {
  DisplayPosition,
  LineId,
  Rail,
  StationId,
  TrackedTrain,
  TrackedTrains,
  TrainId,
  TrainMotion,
} from './types.ts';

export function* prepareMergeTrainsSteps(
  trains: TrackedTrains,
  computed: readonly TrainPrediction[],
  lines: readonly LineId[],
  now: number,
  retired: ReadonlySet<string> = new Set(),
): Generator<void, () => void, void> {
  if (!computed.some((p) => p.journey !== undefined))
    return yield* Core.prepareMergeTrainsSteps(trains, computed, lines, now, retired);
  const keys = new Set(computed.map((p) => p.key));
  const byJourney = new Map(computed.filter((p) => p.journey !== undefined).map((p) => [p.journey, p.key]));
  const byKey = new Map(computed.map((p) => [p.key, p]));
  const staged: TrackedTrains = new Map();
  for (const [id, train] of trains) {
    let key = byJourney.get(train.pred.journey) ?? train.key;
    const owner = byKey.get(key);
    const observedTransition =
      owner?.followObservedPosition === true && train.pred.followObservedPosition !== true && key === train.key;
    if (
      owner?.journey !== undefined &&
      train.pred.journey !== undefined &&
      owner.journey !== train.pred.journey &&
      !observedTransition
    ) {
      key = `missing:${id}`;
      while (keys.has(key)) key += ':';
    }
    staged.set(id, key === train.key ? train : { ...train, key });
    yield;
  }
  const publish = yield* Core.prepareMergeTrainsSteps(staged, computed, lines, now, retired);
  return () => {
    publish();
    const missing: TrainPrediction[] = [];
    for (const [id, prepared] of staged) {
      const live = trains.get(id);
      if (live) {
        live.key = prepared.key;
        live.pred = prepared.pred;
        live.dead = prepared.dead;
        live.missingSince = prepared.missingSince;
      } else if (keys.has(prepared.key)) missing.push(prepared.pred);
    }
    if (missing.length > 0) Core.mergeTrains(trains, missing, [], now, retired);
  };
}

export function mergeTrains(
  trains: TrackedTrains,
  computed: readonly TrainPrediction[],
  lines: readonly LineId[],
  now: number,
  retired: ReadonlySet<string> = new Set(),
): void {
  finish(prepareMergeTrainsSteps(trains, computed, lines, now, retired))();
}

function distance(rail: Rail, from: StationId | null, to: StationId, f: number, near = 0): number | null {
  let best: number | null = null;
  for (let i = 0; i < rail.ids.length; i++) {
    if (rail.ids[i] !== to || (from !== null && rail.ids[i - 1] !== from)) continue;
    const at =
      from === null ? (rail.cum[i] ?? 0) : (rail.cum[i - 1] ?? 0) + f * ((rail.cum[i] ?? 0) - (rail.cum[i - 1] ?? 0));
    if (best === null || Math.abs(at - near) < Math.abs(best - near)) best = at;
  }
  return best;
}

function point(net: Network, line: LineId, rail: Rail, at: number): DisplayPosition {
  for (let i = 1; i < rail.ids.length; i++) {
    const from = rail.ids[i - 1];
    const to = rail.ids[i];
    const end = rail.cum[i] ?? 0;
    if (from === undefined || to === undefined || (at > end && i < rail.ids.length - 1)) continue;
    const start = rail.cum[i - 1] ?? 0;
    const f = Math.max(0, Math.min(1, (at - start) / Math.max(1, end - start)));
    return {
      ...Core.along(net, line, from, to, f),
      P: from,
      N: to,
      f,
      segment: `${line}|${from}|${to}`,
      snapped: false,
    };
  }
  const sid = rail.ids[0] ?? '';
  const station = net.stations.get(sid);
  return {
    lat: station?.lat ?? 0,
    lon: station?.lon ?? 0,
    P: null,
    N: sid,
    f: 1,
    ang: null,
    segment: `${line}|null|${sid}`,
    snapped: false,
  };
}

const frames = new WeakMap<TrainMotion, { readonly at: number; readonly pred: TrainPrediction }>();
const validPredictions = new WeakMap<TrainPrediction, Network>();
const validRails = new WeakMap<Rail, Network>();
const trackers = new WeakMap<
  TrainMotion,
  { at: number; speed: number; behindSince: number | undefined; held: boolean }
>();
const pointStates = new WeakMap<TrainMotion, { speed: number; at: number }>();
const boardSpeeds = new WeakMap<TrainMotion, number>();

function movePoint(
  train: TrackedTrain,
  target: { readonly lat: number; readonly lon: number },
  dt: number,
  observedAt?: number,
  reset = false,
): void {
  const motion = train.motion;
  const previous = motion.pos;
  const meters = previous ? Core.dist(previous, target) : 0;
  const seconds = Math.min(1, Math.max(0, dt) / 1000);
  let state = pointStates.get(motion);
  if (!state) {
    state = { speed: 0, at: 0 };
    pointStates.set(motion, state);
  }
  const dx = previous ? (target.lon - previous.lon) * Math.cos((previous.lat * Math.PI) / 180) : 0;
  const dy = previous ? previous.lat - target.lat : 0;
  const behind =
    previous?.ang !== undefined &&
    previous.ang !== null &&
    dx * Math.cos(previous.ang) + dy * Math.sin(previous.ang) < -1 / 111_195;
  let snap = reset;
  if (observedAt !== undefined && observedAt > state.at) {
    state.at = observedAt;
    snap ||= behind && meters > 500;
  }
  const speed = meters / 10;
  const step = speed * seconds;
  const fraction = snap ? 1 : previous && meters > 0 ? Math.min(1, step / meters) : 1;
  motion.pos = {
    lat: previous ? previous.lat + (target.lat - previous.lat) * fraction : target.lat,
    lon: previous ? previous.lon + (target.lon - previous.lon) * fraction : target.lon,
    P: null,
    N: train.pred.N,
    f: 1,
    ang: snap ? null : meters > 0.01 ? Math.atan2(dy, dx) : (previous?.ang ?? null),
    segment: `${train.pred.followObservedPosition === true ? 'observed' : 'estimated'}|${train.id}`,
    snapped: snap && !reset ? 'route' : false,
  };
  motion.moving = previous !== undefined && meters > 0.01 && fraction > 0;
  motion.rail = undefined;
  motion.sd = undefined;
  motion.vd = 0;
  motion.motionReason = reset ? 'unbound' : snap ? 'distance-correction' : meters <= 0.01 ? 'source-still' : 'unbound';
  state.speed = fraction === 1 ? 0 : speed;
}

export function advanceTrains(
  net: Network,
  trains: TrackedTrains,
  now: number,
  dt: number,
  reset = false,
  near?: (t: TrackedTrain, meters: number) => boolean,
): TrainId[] {
  const held: (readonly [TrainId, TrackedTrain])[] = [];
  const retired: TrainId[] = [];
  for (const [id, train] of trains) {
    const pred = train.pred;
    if (pred.motionTracker === true && pred.gpsPoint !== undefined) {
      trains.delete(id);
      trackers.delete(train.motion);
      movePoint(train, pred.gpsPoint, dt, pred.gpsPoint.at, reset);
      train.alpha += ((train.dead ? 0 : 1) - train.alpha) * Math.min(1, dt / 500);
      if (train.dead && train.alpha < 0.02) retired.push(id);
      else held.push([id, train]);
      continue;
    }
    const rail = train.motion.rail;
    let known = validPredictions.get(pred) === net;
    if (!known) {
      const refs = [pred.N, ...pred.stops.map((stop) => stop.sid)];
      if (pred.P !== null && pred.P !== '') refs.push(pred.P);
      if (pred.gpsMotion) refs.push(pred.gpsMotion.from, pred.gpsMotion.to);
      if (pred.holdAt !== undefined) refs.push(pred.holdAt);
      known = refs.every((sid) => net.stations.has(sid));
      if (known) validPredictions.set(pred, net);
    }
    if (known && rail !== undefined && validRails.get(rail) !== net) {
      if (rail.ids.every((sid) => net.stations.has(sid))) validRails.set(rail, net);
      else {
        const motion = train.motion;
        const ids = rail.ids.filter((sid) => net.stations.has(sid));
        const cum = [0];
        for (let i = 1; i < ids.length; i++)
          cum.push((cum.at(-1) ?? 0) + Core.segLength(net, pred.lineId, ids[i - 1] ?? '', ids[i] ?? ''));
        const rebuilt = { ids, cum };
        const prior = motion.pos ? distance(rebuilt, motion.pos.P, motion.pos.N, motion.pos.f, motion.sd) : null;
        let nearest: { readonly at: number; readonly d: number } | undefined;
        if (prior === null && motion.pos !== undefined)
          for (let i = 1; i < ids.length; i++) {
            const from = ids[i - 1] ?? '';
            const to = ids[i] ?? '';
            const projected = Core.projectOnto(net, pred.lineId, from, to, motion.pos);
            if (nearest === undefined || projected.d < nearest.d)
              nearest = { at: (cum[i - 1] ?? 0) + projected.f * ((cum[i] ?? 0) - (cum[i - 1] ?? 0)), d: projected.d };
          }
        motion.sd = prior ?? nearest?.at;
        motion.rail = motion.sd === undefined ? undefined : rebuilt;
        motion.railStops = undefined;
        motion.dwell = undefined;
        motion.owed = undefined;
        motion.vd = 0;
      }
    }
    if (known) continue;
    trains.delete(id);
    const sid = net.stations.has(pred.N) ? pred.N : pred.stops.find((stop) => net.stations.has(stop.sid))?.sid;
    const station = sid === undefined ? undefined : net.stations.get(sid);
    if (train.motion.pos === undefined && station !== undefined)
      train.motion.pos = {
        lat: station.lat,
        lon: station.lon,
        P: null,
        N: station.id,
        f: 1,
        ang: null,
        segment: `held|${id}`,
        snapped: false,
      };
    train.motion.moving = false;
    train.alpha += ((train.dead ? 0 : 1) - train.alpha) * Math.min(1, dt / 500);
    if (train.dead && train.alpha < 0.02) retired.push(id);
    else held.push([id, train]);
  }
  let before: Map<TrainId, DisplayPosition | undefined> | undefined;
  for (const [id, train] of trains)
    if (train.pred.gpsMotion || train.pred.holdAt !== undefined || train.pred.motionTracker === true) {
      before ??= new Map();
      before.set(id, train.motion.pos);
    }
  const removed = [...Core.advanceTrains(net, trains, now, dt, reset, near), ...retired];
  for (const [id, train] of held) trains.set(id, train);
  if (!before) return removed;
  for (const [id, previous] of before) {
    const train = trains.get(id);
    if (!train || (!reset && previous !== undefined && train.motion.pos === previous)) continue;
    const { pred, motion } = train;
    let rail = motion.rail;
    const fix = pred.gpsMotion;
    const last = frames.get(motion);
    const step = near !== undefined && last?.pred === pred ? Math.max(dt, now - last.at) : dt;
    frames.set(motion, { at: now, pred });
    if (fix) {
      const hop = Core.segLength(net, pred.lineId, fix.from, fix.to);
      const elapsed = Math.min(
        pred.followEstimatedPosition === true || pred.followObservedPosition === true ? fix.maxS / 4 : fix.maxS,
        Math.max(0, (now - fix.at) / 1000),
      );
      const f = Math.min(1, fix.f + (elapsed * fix.speed) / Math.max(1, hop));
      if (!rail || distance(rail, fix.from, fix.to, f, motion.sd) === null) {
        const ids = [fix.from, fix.to];
        for (const stop of pred.stops) if (ids.at(-1) !== stop.sid) ids.push(stop.sid);
        if (
          previous !== undefined &&
          previous.P !== null &&
          previous.P !== '' &&
          previous.N === fix.from &&
          previous.P !== fix.from
        )
          ids.unshift(previous.P);
        const cum = [0];
        for (let i = 1; i < ids.length; i++)
          cum.push((cum.at(-1) ?? 0) + Core.segLength(net, pred.lineId, ids[i - 1] ?? '', ids[i] ?? ''));
        rail = { ids, cum };
        motion.rail = rail;
        motion.railStops = pred.stops;
      }
      const target = distance(rail, fix.from, fix.to, f, motion.sd) ?? 0;
      const capF = Math.min(1, fix.f + (fix.capSpeed * fix.maxS) / Math.max(1, hop));
      const cap = distance(rail, fix.from, fix.to, capF, target) ?? target;
      let prior = previous ? distance(rail, previous.P, previous.N, previous.f, target) : null;
      if (prior === null && previous) {
        const projected = Core.projectOnto(net, pred.lineId, fix.from, fix.to, previous);
        if (projected.d <= 300) prior = distance(rail, fix.from, fix.to, projected.f, target);
      }
      const start = prior ?? target;
      if (fix.scheduledSpeed !== undefined && fix.nextStopDistance !== undefined) {
        if (previous && (prior === null || Core.dist(previous, point(net, pred.lineId, rail, start)) > 5)) {
          motion.pos = previous;
          movePoint(train, point(net, pred.lineId, rail, target), step, fix.at, reset);
          continue;
        }
        let tracker = trackers.get(motion);
        const raw = distance(rail, fix.from, fix.to, fix.f, start) ?? start;
        const end = raw + fix.nextStopDistance;
        const projectedTarget = Math.min(end, raw + Math.min(90, Math.max(0, (now - fix.at) / 1000)) * fix.speed);
        if (pred.followEstimatedPosition === true || pred.followObservedPosition === true) {
          const seconds = Math.min(1, Math.max(0, step) / 1000);
          const followTarget = Math.min(end, raw + elapsed * fix.speed);
          const gap = followTarget - start;
          const initializing = reset || previous === undefined;
          if (!tracker) {
            tracker = { at: fix.at, speed: 0, behindSince: undefined, held: false };
            trackers.set(motion, tracker);
          }
          if (initializing || gap >= 0) tracker.behindSince = undefined;
          else if (fix.at > tracker.at) tracker.behindSince ??= fix.at;
          tracker.at = fix.at;
          const correcting =
            gap < 0 && tracker.behindSince !== undefined && fix.at - tracker.behindSince >= 3 * fix.maxS * 1000;
          const waitingForConfirmation = !initializing && gap < 0 && !correcting;
          const movement = waitingForConfirmation
            ? 0
            : Math.min(Math.abs(gap) * -Math.expm1(-seconds / Math.max(1, fix.maxS)), 25 * seconds);
          const next = initializing ? followTarget : start + Math.sign(gap) * movement;
          pointStates.delete(motion);
          motion.pos = point(net, pred.lineId, rail, next);
          motion.sd = next;
          motion.vd = 0;
          motion.moving = previous !== undefined && Math.abs(next - start) > 1e-7;
          motion.motionReason = waitingForConfirmation ? 'held-behind' : correcting ? 'estimate-correction' : 'moving';
          continue;
        }
        if (!tracker) {
          tracker = {
            at: fix.at,
            speed:
              previous === undefined ? fix.speed : (pointStates.get(motion)?.speed ?? boardSpeeds.get(motion) ?? 0),
            held: false,
            behindSince: undefined,
          };
          trackers.set(motion, tracker);
        }
        pointStates.delete(motion);
        let snap = reset;
        if (fix.at > tracker.at) {
          tracker.at = fix.at;
          tracker.held = projectedTarget < start - 0.1;
          if (tracker.held) {
            tracker.behindSince ??= fix.at;
            snap ||= start - raw > 500 || fix.at - tracker.behindSince >= 180_000;
          } else {
            tracker.behindSince = undefined;
          }
        }
        let next = start;
        if (snap) {
          next = reset ? projectedTarget : raw;
          tracker.speed = 0;
          tracker.held = false;
          tracker.behindSince = undefined;
        } else if (tracker.held || projectedTarget <= start || now - fix.at >= 90_000) tracker.speed = 0;
        else {
          const seconds = Math.min(1, Math.max(0, step) / 1000);
          const pace =
            fix.measuredSpeed === undefined ? fix.speed : Math.min(fix.scheduledSpeed, fix.measuredSpeed) / 2;
          const catchUpTarget = fix.measuredSpeed === undefined ? projectedTarget : raw;
          const speed = Math.max(pace, Math.max(0, catchUpTarget - start) / 10);
          next = Math.min(projectedTarget, start + speed * seconds);
          tracker.speed = speed;
        }
        motion.pos = { ...point(net, pred.lineId, rail, next), snapped: previous && snap && !reset ? 'route' : false };
        motion.sd = next;
        motion.vd = 0;
        motion.moving = previous !== undefined && Math.abs(next - start) > 1e-7;
        motion.motionReason = reset
          ? 'moving'
          : snap
            ? Math.abs(start - raw) > 500
              ? 'distance-correction'
              : 'persistent-correction'
            : tracker.held
              ? 'held-behind'
              : now - fix.at >= 90_000
                ? 'stale'
                : start >= end - 0.1
                  ? 'next-stop'
                  : fix.speed === 0
                    ? 'source-still'
                    : tracker.speed > fix.speed + 0.1
                      ? 'catch-up'
                      : 'moving';
        continue;
      }
      const snap = reset || prior === null || Math.abs(target - start) > 300;
      const next = Math.min(cap, snap ? target : start + (target - start) * Math.min(1, Math.max(0, step) / 2000));
      motion.pos = { ...point(net, pred.lineId, rail, next), snapped: previous && snap && !reset ? 'route' : false };
      motion.sd = next;
      motion.vd = 0;
    } else if (rail && pred.holdAt !== undefined) {
      const stop = pred.stops.find(
        (s) => s.sid === pred.holdAt && (pred.holdTime === undefined || (s.dep ?? s.eta) === pred.holdTime),
      );
      const cap = stop ? distance(rail, null, stop.sid, 1, motion.sd) : null;
      if (cap !== null && (motion.sd ?? 0) > cap) {
        motion.pos = point(net, pred.lineId, rail, cap);
        motion.sd = cap;
        motion.vd = 0;
      }
    }
    if (!fix && pred.motionTracker === true && previous && rail && motion.pos && !reset) {
      trackers.delete(motion);
      const goal = motion.sd ?? 0;
      const prior = distance(rail, previous.P, previous.N, previous.f, goal);
      if (prior === null || Core.dist(previous, point(net, pred.lineId, rail, prior)) > 5) {
        const target = motion.pos;
        motion.pos = previous;
        movePoint(train, target, step, undefined, reset);
        continue;
      }
      pointStates.delete(motion);
      const seconds = Math.min(1, Math.max(0, step) / 1000);
      const oldSpeed = boardSpeeds.get(motion) ?? 0;
      const capSpeed = pred.lineId.startsWith('train-') ? 60 : 35;
      const speed = goal <= prior ? 0 : Math.min(capSpeed, oldSpeed + 1.5 * seconds);
      const next = Math.max(prior, Math.min(goal, prior + ((oldSpeed + speed) / 2) * seconds));
      motion.pos = point(net, pred.lineId, rail, next);
      motion.sd = next;
      motion.vd = Math.min(speed, Math.max(0, motion.vd ?? 0));
      boardSpeeds.set(motion, speed);
      motion.motionReason = goal < prior - 0.1 ? 'held-behind' : goal <= prior ? 'source-still' : 'moving';
    }
    if (!fix && pred.motionTracker === true && reset && rail && motion.pos) {
      pointStates.delete(motion);
      boardSpeeds.delete(motion);
    }
    const current = motion.pos;
    motion.moving =
      previous !== undefined &&
      current !== undefined &&
      (previous.segment !== current.segment || Math.abs(previous.f - current.f) > 1e-7);
  }
  return removed;
}
